import { TextKey, RouteName, FeatureFlag } from '@mews-commander/enums';
import { useFeatureFlag, useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { compactArray } from '@mews/fp';
import {
    isCurrentEnterpriseServiceProviderWithBookableService,
    areCurrentEnterpriseCalendarEventsEnabled,
    areCurrentEnterpriseCustomLinksEnabled,
} from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { canCurrentProfileManageServiceConfigurations } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { AssistantNavigation } from './AssistantNavigation';
import { useNavigationSearchResult } from './useNavigationSearchResult';
import { useNavigationPath } from './useNavigationPath';

export const useEnterpriseNavigation = (): AssistantNavigation[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const canManageServiceConfiguration = useCommanderSelector<boolean>(canCurrentProfileManageServiceConfigurations);
    const isServiceProvider = useCommanderSelector((state) =>
        isCurrentEnterpriseServiceProviderWithBookableService(state)
    );
    const linksEnabled = useCommanderSelector<boolean>(areCurrentEnterpriseCustomLinksEnabled);
    const eventsEnabled = useCommanderSelector<boolean>(areCurrentEnterpriseCalendarEventsEnabled);

    const localizeText = useLocalizeText();
    const newRoutingRulesOnAddEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesOnAddEnabled);

    const parentPath = `${localizeText({ textKey: TextKey.Enterprise })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    const params = { query: { OwnerId: enterpriseId } };

    const billingAutomationResult = useNavigationSearchResult(
        RouteName.RoutingRuleIndex,
        TextKey.BillingAutomationPlural,
        params,
        useNavigationPath(parentPath, TextKey.BillingAutomationPlural)
    );

    const routingRuleResult = useNavigationSearchResult(
        RouteName.RoutingRuleIndex,
        TextKey.RoutingRulePlural,
        params,
        useNavigationPath(parentPath, TextKey.RoutingRulePlural)
    );

    const combinedRoutingRuleResult = compactArray([
        routingRuleResult,
        newRoutingRulesOnAddEnabled ? billingAutomationResult : null,
    ]);
    const accountingCategoryResult = useNavigationSearchResult(
        RouteName.AccountingCategoryIndex,
        TextKey.AccountingCategoryPlural,
        params,
        useNavigationPath(parentPath, TextKey.AccountingCategoryPlural)
    );
    const accountingConfigurationResult = useNavigationSearchResult(
        RouteName.AccountingConfigurationDetail,
        TextKey.AccountingConfiguration,
        { enterpriseId },
        useNavigationPath(parentPath, TextKey.AccountingConfiguration)
    );
    const currencyAcceptanceResult = useNavigationSearchResult(
        RouteName.CurrencyAcceptanceIndex,
        TextKey.CurrencyAcceptancePlural,
        params,
        useNavigationPath(parentPath, TextKey.CurrencyAcceptancePlural)
    );
    const cashierResult = useNavigationSearchResult(
        RouteName.CashierIndex,
        TextKey.CashierPlural,
        params,
        useNavigationPath(parentPath, TextKey.CashierPlural)
    );
    const counterResult = useNavigationSearchResult(
        RouteName.CounterIndex,
        TextKey.CounterPlural,
        params,
        useNavigationPath(parentPath, TextKey.CounterPlural)
    );
    const departmentResult = useNavigationSearchResult(
        RouteName.DepartmentIndex,
        TextKey.DepartmentPlural,
        params,
        useNavigationPath(parentPath, TextKey.DepartmentPlural)
    );
    const resourceResult = useNavigationSearchResult(
        RouteName.ResourceIndex,
        TextKey.SpacePlural,
        params,
        useNavigationPath(parentPath, TextKey.SpacePlural)
    );
    const outletResult = useNavigationSearchResult(
        RouteName.OutletIndex,
        TextKey.OutletPlural,
        params,
        useNavigationPath(parentPath, TextKey.OutletPlural)
    );
    const deviceHubResult = useNavigationSearchResult(
        RouteName.DeviceHubIndex,
        TextKey.DeviceHubPlural,
        params,
        useNavigationPath(parentPath, TextKey.DeviceHubPlural)
    );
    const customLinkResult = useNavigationSearchResult(
        RouteName.CustomLinkIndex,
        TextKey.CustomLinkPlural,
        params,
        useNavigationPath(parentPath, TextKey.CustomLinkPlural)
    );
    const calendarEventResult = useNavigationSearchResult(
        RouteName.CalendarEventIndex,
        TextKey.CalendarEventPlural,
        params,
        useNavigationPath(parentPath, TextKey.CalendarEventPlural)
    );
    const mailTemplateResult = useNavigationSearchResult(
        RouteName.MailTemplateIndex,
        TextKey.MailTemplatePlural,
        params,
        useNavigationPath(parentPath, TextKey.MailTemplatePlural)
    );

    const serviceProviderResults = !isServiceProvider
        ? []
        : [
              ...combinedRoutingRuleResult,
              accountingCategoryResult,
              accountingConfigurationResult,
              currencyAcceptanceResult,
              cashierResult,
              counterResult,
              departmentResult,
              resourceResult,
              outletResult,
              deviceHubResult,
              linksEnabled ? customLinkResult : null,
              eventsEnabled ? calendarEventResult : null,
          ];

    return [...serviceProviderResults, canManageServiceConfiguration ? mailTemplateResult : null].filter(
        Boolean
    ) as AssistantNavigation[];
};
